// /opt/isp_erp/frontend/src/pages/admin/clientes/components/OnuCliente.jsx
import React, { useState, useEffect, useMemo } from "react";
import axios from "../../../../api/axios";
import "./OnuCliente.css";

export default function OnuCliente({ customerId, onuId, onActualizacion }) {
  const [onu, setOnu] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLiberarModal, setShowLiberarModal] = useState(false);
  const [showAsignarModal, setShowAsignarModal] = useState(false);
  const [onusLibres, setOnusLibres] = useState([]);
  const [selectedOnu, setSelectedOnu] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [procesando, setProcesando] = useState(false);

  // Cargar datos de la ONU actual
  useEffect(() => {
    if (onuId) {
      fetchOnu();
    } else {
      setLoading(false);
      setOnu(null);
    }
  }, [onuId]);

  const fetchOnu = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log(`Cargando ONU con ID: ${onuId}`);
      const response = await axios.get(`/olts/onus/${onuId}`);
      console.log("ONU cargada:", response.data);
      setOnu(response.data);
    } catch (error) {
      console.error("Error cargando ONU:", error);
      setError(error.response?.data?.detail || "Error al cargar la ONU");
      setOnu(null);
    } finally {
      setLoading(false);
    }
  };

  // Cargar ONUs libres
  const fetchOnusLibres = async () => {
    try {
      const response = await axios.get("/customers/onus-free");
      console.log("ONUs libres cargadas:", response.data.length);
      setOnusLibres(response.data);
    } catch (error) {
      console.error("Error cargando ONUs libres:", error);
      alert("Error al cargar ONUs disponibles");
    }
  };

  // Filtrar ONUs libres
  const filteredOnusLibres = useMemo(() => {
    if (!searchTerm.trim()) return onusLibres;

    const term = searchTerm.toLowerCase().trim();
    return onusLibres.filter(onu => {
      return (
        (onu.id?.toString().toLowerCase().includes(term)) ||
        (onu.onu_id?.toString().toLowerCase().includes(term)) ||
        (onu.name?.toLowerCase().includes(term)) ||
        (onu.olt_id?.toString().toLowerCase().includes(term)) ||
        (onu.pon?.toLowerCase().includes(term))
      );
    });
  }, [onusLibres, searchTerm]);

  // Liberar ONU
  const handleLiberarOnu = async () => {
    setProcesando(true);
    try {
      await axios.post(`/customers/${customerId}/liberar-onu`);
      alert("✅ ONU liberada exitosamente");
      setShowLiberarModal(false);
      setOnu(null);
      if (onActualizacion) onActualizacion();
    } catch (error) {
      console.error("Error liberando ONU:", error);
      alert(error.response?.data?.detail || "Error al liberar ONU");
    } finally {
      setProcesando(false);
    }
  };

  // Asignar nueva ONU
  const handleAsignarOnu = async () => {
    if (!selectedOnu) {
      alert("Debe seleccionar una ONU");
      return;
    }

    setProcesando(true);
    try {
      await axios.post(`/customers/${customerId}/asignar-onu`, {
        onu_id: selectedOnu.id
      });
      alert("✅ ONU asignada exitosamente");
      setShowAsignarModal(false);
      setSelectedOnu(null);
      setSearchTerm("");
      await fetchOnu(); // Recargar la ONU asignada
      if (onActualizacion) onActualizacion();
    } catch (error) {
      console.error("Error asignando ONU:", error);
      alert(error.response?.data?.detail || "Error al asignar ONU");
    } finally {
      setProcesando(false);
    }
  };

  const abrirModalAsignar = () => {
    fetchOnusLibres();
    setShowAsignarModal(true);
  };

  if (loading) {
    return (
      <div className="onu-loading-container">
        <div className="onu-loading-spinner"></div>
        <p>Cargando información de la ONU...</p>
      </div>
    );
  }

  if (error && !onu) {
    return (
      <div className="onu-error-container">
        <div className="onu-error-card">
          <div className="onu-error-icon">⚠️</div>
          <p className="onu-error-title">Error al cargar ONU</p>
          <p className="onu-error-message">{error}</p>
          <button onClick={abrirModalAsignar} className="onu-btn-primary">
            Asignar Nueva ONU
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="onu-cliente">
      {/* Header con título y botones de acción */}
      <div className="onu-header">
        <div className="onu-header-left">
          <div className="onu-header-icon">📡</div>
          <div>
            <h2>ONU Asignada</h2>
            <p>Información del dispositivo del cliente</p>
          </div>
        </div>
        <div className="onu-header-actions">
          {onu && (
            <button
              onClick={() => setShowLiberarModal(true)}
              className="onu-btn-warning"
            >
              🔓 Liberar ONU
            </button>
          )}
          <button
            onClick={abrirModalAsignar}
            className="onu-btn-primary"
          >
            {onu ? "🔄 Reasignar ONU" : "➕ Asignar ONU"}
          </button>
        </div>
      </div>

      {/* Contenido principal */}
      {!onu ? (
        <div className="onu-empty-state">
          <div className="onu-empty-icon">📡</div>
          <h3>No hay ONU asignada</h3>
          <p>Este cliente no tiene una ONU asignada actualmente.</p>
          <button onClick={abrirModalAsignar} className="onu-btn-primary">
            Asignar ONU
          </button>
        </div>
      ) : (
        <div className="onu-card">
          <div className="onu-card-header">
            <h3>Detalles de la ONU</h3>
          </div>
          <div className="onu-card-body">
            <div className="onu-details-grid">
              {/* Columna izquierda */}
              <div className="onu-details-column">
                <div className="onu-detail-item">
                  <span className="onu-detail-label">ID ONU (BD)</span>
                  <span className="onu-detail-value">{onu.id}</span>
                </div>
                <div className="onu-detail-item">
                  <span className="onu-detail-label">OLT ID</span>
                  <span className="onu-detail-value">{onu.olt_id}</span>
                </div>
                <div className="onu-detail-item">
                  <span className="onu-detail-label">PON</span>
                  <span className="onu-detail-value">{onu.pon}</span>
                </div>
                <div className="onu-detail-item">
                  <span className="onu-detail-label">Índice ONU</span>
                  <span className="onu-detail-value">{onu.onu_id}</span>
                </div>
              </div>

              {/* Columna derecha */}
              <div className="onu-details-column">
                <div className="onu-detail-item">
                  <span className="onu-detail-label">Modelo</span>
                  <span className="onu-detail-value">{onu.model || 'N/A'}</span>
                </div>
                <div className="onu-detail-item">
                  <span className="onu-detail-label">Serial</span>
                  <span className="onu-detail-value">{onu.serial || 'N/A'}</span>
                </div>
                <div className="onu-detail-item">
                  <span className="onu-detail-label">Nombre</span>
                  <span className="onu-detail-value">{onu.name || 'N/A'}</span>
                </div>
                <div className="onu-detail-item">
                  <span className="onu-detail-label">Estado operativo</span>
                  <span className={`onu-status-badge onu-status-${onu.oper_state}`}>
                    {onu.oper_state || 'N/A'}
                  </span>
                </div>
                {onu.rx_power && (
                  <div className="onu-detail-item">
                    <span className="onu-detail-label">RX Power</span>
                    <span className="onu-detail-value">{onu.rx_power} dBm</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal para liberar ONU */}
      {showLiberarModal && (
        <div className="onu-modal-overlay">
          <div className="onu-modal-content onu-modal-small">
            <div className="onu-modal-header">
              <div className="onu-modal-header-icon">🔓</div>
              <h3>¿Liberar ONU?</h3>
            </div>
            <div className="onu-modal-body">
              <p className="onu-modal-text">
                ¿Está seguro que desea liberar la ONU del cliente?
                El cliente quedará sin servicio hasta que se asigne una nueva ONU.
              </p>
            </div>
            <div className="onu-modal-footer">
              <button
                onClick={() => setShowLiberarModal(false)}
                className="onu-btn-secondary"
                disabled={procesando}
              >
                Cancelar
              </button>
              <button
                onClick={handleLiberarOnu}
                className="onu-btn-warning"
                disabled={procesando}
              >
                {procesando ? "Procesando..." : "Liberar ONU"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para asignar/reasignar ONU */}
      {showAsignarModal && (
        <div className="onu-modal-overlay">
          <div className="onu-modal-content onu-modal-large">
            <div className="onu-modal-header">
              <div className="onu-modal-header-icon">📡</div>
              <div>
                <h3>{onu ? "Reasignar ONU" : "Asignar Nueva ONU"}</h3>
                <p className="onu-modal-subtitle">
                  Seleccione una ONU disponible para asignar al cliente
                </p>
              </div>
            </div>

            <div className="onu-modal-body">
              {/* Barra de búsqueda */}
              <div className="onu-search-container">
                <input
                  type="text"
                  placeholder="Buscar ONU por ID, nombre, OLT, PON..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="onu-search-input"
                />
                <div className="onu-search-info">
                  <span>{filteredOnusLibres.length} ONUs disponibles</span>
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="onu-search-clear"
                    >
                      Limpiar búsqueda
                    </button>
                  )}
                </div>
              </div>

              {/* Lista de ONUs */}
              <div className="onu-list-container">
                {filteredOnusLibres.length === 0 ? (
                  <div className="onu-empty-list">
                    <p>📡</p>
                    <p>{searchTerm ? "No se encontraron ONUs" : "No hay ONUs disponibles para asignar"}</p>
                  </div>
                ) : (
                  filteredOnusLibres.map((onuItem) => (
                    <div
                      key={onuItem.id}
                      className={`onu-list-item ${selectedOnu?.id === onuItem.id ? 'selected' : ''}`}
                      onClick={() => setSelectedOnu(onuItem)}
                    >
                      <div className="onu-item-info">
                        <div className="onu-item-badges">
                          <span className="onu-badge olt-badge">OLT {onuItem.olt_id}</span>
                          <span className="onu-badge pon-badge">PON {onuItem.pon}</span>
                          <span className="onu-badge id-badge">ID: {onuItem.id}</span>
                          <span className="onu-badge index-badge">Índice: {onuItem.onu_id}</span>
                        </div>
                        <p className="onu-item-name">{onuItem.name || 'Sin nombre'}</p>
                      </div>
                      {selectedOnu?.id === onuItem.id && (
                        <span className="onu-item-selected">✓ Seleccionada</span>
                      )}
                    </div>
                  ))
                )}
              </div>

              {/* Resumen de selección */}
              {selectedOnu && (
                <div className="onu-selection-summary">
                  <p className="onu-summary-title">ONU seleccionada:</p>
                  <p className="onu-summary-details">
                    OLT {selectedOnu.olt_id} | PON {selectedOnu.pon} | 
                    Índice: {selectedOnu.onu_id} | {selectedOnu.name || ''}
                  </p>
                </div>
              )}
            </div>

            <div className="onu-modal-footer">
              <button
                onClick={() => {
                  setShowAsignarModal(false);
                  setSelectedOnu(null);
                  setSearchTerm("");
                }}
                className="onu-btn-secondary"
                disabled={procesando}
              >
                Cancelar
              </button>
              <button
                onClick={handleAsignarOnu}
                className="onu-btn-primary"
                disabled={!selectedOnu || procesando}
              >
                {procesando ? "Asignando..." : "Asignar ONU"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
